import { BadgeDollarSign, Check, ListTodo, Sparkles, WalletCards, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useApp } from "../../context/AppContext";
import { api } from "../../lib/api";
import { Button } from "../ui";

const draftKey = "earth-online:v3:quick-draft";

export function QuickCaptureModal() {
  const { data, closeModal, mutate } = useApp();
  const [mode, setMode] = useState<"task" | "asset" | "transaction">("task");
  const [title, setTitle] = useState(() => window.localStorage.getItem(draftKey) ?? "");
  const [detail, setDetail] = useState("");
  const [projectId, setProjectId] = useState("");
  const [skillId, setSkillId] = useState("");
  const [contactId, setContactId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [completed, setCompleted] = useState(false);
  const [assetName, setAssetName] = useState("现金");
  const [assetType, setAssetType] = useState("CASH");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("CNY");
  const [transactionType, setTransactionType] = useState("EXPENSE");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    if (title) window.localStorage.setItem(draftKey, title);
    else window.localStorage.removeItem(draftKey);
  }, [title]);

  const financeMatch = title.match(/(?:现金|余额|资产)[^\d]*(\d+(?:\.\d{1,2})?)/);

  const submit = async () => {
    let saved;
    if (mode === "task") {
      saved = await mutate(async () => {
        const created = await api.create("tasks", {
          title,
          detail,
          projectId,
          goalId: "",
          skillId,
          contactId,
          dueDate,
          priority: 3,
          valueScore: 3,
          difficulty: 2,
          estimatedMinutes: null,
          pinned: true,
          externalCommitment: false
        });
        if (completed) await api.completeTask(created.id);
        return created;
      }, completed ? "事项已完成并进入时间线" : "事项已加入今日待办");
    } else if (mode === "asset") {
      saved = await mutate(
        () => api.create("assets", {
          name: assetName,
          assetType,
          amount,
          currency,
          note: title,
          asOfDate: today
        }),
        "资产余额已更新，财富雷达已同步"
      );
    } else {
      saved = await mutate(
        () => api.create("transactions", {
          accountType: "PERSONAL",
          transactionType,
          amount,
          currency,
          category,
          subcategory,
          note: title,
          occurredOn: today
        }),
        "财务流水已记录"
      );
    }
    if (saved) {
      window.localStorage.removeItem(draftKey);
      closeModal();
    }
  };

  const disabled =
    (mode === "task" && !title.trim()) ||
    (mode === "asset" && (!assetName.trim() || !amount)) ||
    (mode === "transaction" && (!amount || !category.trim()));

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={closeModal}>
      <section
        className="modal modal--wide"
        role="dialog"
        aria-modal="true"
        aria-label="快速添加"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="modal__header">
          <div>
            <h2>快速添加</h2>
            <p>普通记录先进入事项；只有完成事项才进入时间线。财务与资产使用独立账本。</p>
          </div>
          <button className="modal__close" onClick={closeModal} aria-label="关闭">
            <X size={20} />
          </button>
        </header>

        <div className="capture-type-tabs">
          <button className={mode === "task" ? "active" : ""} onClick={() => setMode("task")}>
            <ListTodo size={16} /> 待办事项
          </button>
          <button className={mode === "asset" ? "active" : ""} onClick={() => setMode("asset")}>
            <WalletCards size={16} /> 资产余额
          </button>
          <button className={mode === "transaction" ? "active" : ""} onClick={() => setMode("transaction")}>
            <BadgeDollarSign size={16} /> 收支流水
          </button>
        </div>

        <div className="form-grid form-grid--two modal__body quick-entry-form">
          {mode === "task" && (
            <>
              <label className="field field--full">
                <span>要做什么 <b>*</b></span>
                <textarea
                  autoFocus
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="例如：整理本周客户报价；完成后再进入时间线"
                  rows={3}
                />
              </label>
              {financeMatch && (
                <button
                  className="finance-detection field--full"
                  onClick={() => {
                    setAssetName(title.includes("现金") ? "现金" : "当前资产");
                    setAmount(financeMatch[1] ?? "");
                    setMode("asset");
                  }}
                >
                  <Sparkles size={16} />
                  识别到金额 {financeMatch[1]} 元，改为“资产余额”记录
                </button>
              )}
              <label className="field field--full">
                <span>完成标准或备注</span>
                <textarea value={detail} onChange={(event) => setDetail(event.target.value)} rows={2} />
              </label>
              <label className="field">
                <span>关联项目</span>
                <select value={projectId} onChange={(event) => setProjectId(event.target.value)}>
                  <option value="">不关联</option>
                  {data?.projects.map((project) => <option key={project.id} value={project.id}>{project.title}</option>)}
                </select>
              </label>
              <label className="field">
                <span>截止日期</span>
                <input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} />
              </label>
              <label className="field">
                <span>关联技能或兴趣</span>
                <select value={skillId} onChange={(event) => setSkillId(event.target.value)}>
                  <option value="">不关联</option>
                  {data?.skills.map((skill) => <option key={skill.id} value={skill.id}>{skill.title}</option>)}
                </select>
              </label>
              <label className="field">
                <span>关联联系人</span>
                <select value={contactId} onChange={(event) => setContactId(event.target.value)}>
                  <option value="">不关联</option>
                  {data?.contacts.map((contact) => <option key={contact.id} value={contact.id}>{contact.name}</option>)}
                </select>
              </label>
              <label className="completion-toggle field--full">
                <input type="checkbox" checked={completed} onChange={(event) => setCompleted(event.target.checked)} />
                <span>
                  <strong>这件事已经完成</strong>
                  <small>勾选后会立即归档到时间线；否则留在今日事项中。</small>
                </span>
              </label>
            </>
          )}

          {mode === "asset" && (
            <>
              <label className="field">
                <span>资产名称 <b>*</b></span>
                <input autoFocus value={assetName} onChange={(event) => setAssetName(event.target.value)} />
              </label>
              <label className="field">
                <span>资产类型</span>
                <select value={assetType} onChange={(event) => setAssetType(event.target.value)}>
                  <option value="CASH">现金</option><option value="BANK">银行账户</option>
                  <option value="INVESTMENT">投资</option><option value="RECEIVABLE">应收款</option>
                  <option value="FIXED">固定资产</option><option value="OTHER">其他</option>
                </select>
              </label>
              <label className="field">
                <span>当前余额 <b>*</b></span>
                <input inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="3000.00" />
              </label>
              <label className="field">
                <span>币种</span>
                <select value={currency} onChange={(event) => setCurrency(event.target.value)}>
                  {["CNY", "USD", "EUR", "HKD", "JPY"].map((item) => <option key={item}>{item}</option>)}
                </select>
              </label>
              <label className="field field--full">
                <span>备注</span>
                <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="例如：7月现金盘点" />
              </label>
            </>
          )}

          {mode === "transaction" && (
            <>
              <label className="field">
                <span>类型</span>
                <select value={transactionType} onChange={(event) => setTransactionType(event.target.value)}>
                  <option value="EXPENSE">支出</option><option value="INCOME">收入</option>
                </select>
              </label>
              <label className="field">
                <span>金额 <b>*</b></span>
                <input autoFocus inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} />
              </label>
              <label className="field">
                <span>大类 <b>*</b></span>
                <input value={category} onChange={(event) => setCategory(event.target.value)} placeholder="生活 / 工作 / 成长" />
              </label>
              <label className="field">
                <span>子类目</span>
                <input value={subcategory} onChange={(event) => setSubcategory(event.target.value)} placeholder="餐饮、软件、项目回款…" />
              </label>
              <label className="field field--full">
                <span>备注</span>
                <input value={title} onChange={(event) => setTitle(event.target.value)} />
              </label>
            </>
          )}
        </div>

        <div className="modal__footer">
          <Button variant="ghost" onClick={closeModal}>取消</Button>
          <Button onClick={() => void submit()} disabled={disabled} icon={<Check size={16} />}>
            {mode === "task" ? (completed ? "完成并归档" : "加入今日事项") : "保存"}
          </Button>
        </div>
      </section>
    </div>
  );
}
